import { FC, useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  GroupBase,
  OptionProps,
  SingleValueProps,
  components,
} from 'react-select'

import {
  CalculateRequest,
  FormExchangeModel,
  ICreateExchangeRequest,
  IExchangeResponse,
  IFiat,
} from '@/core/interfaces/exchange'
import { exchangeSelector } from '@/core/services/App/AppSelector'
import {
  calculationThunk,
  createExchangeThunk,
  getCourseThunk,
} from '@/core/services/App/AppSlice'
import { useAppDispatch, useAppSelector } from '@/core/store/hooks'
import { userSelector } from '@/modules/Auth/store/AuthSelector'
import { cn } from '@bem-react/classname'
import { motion } from 'framer-motion'
import _ from 'lodash'

import { useHandleServerError } from '@/hooks/useHandleServerError'

import CustomSelect from '@/components/Form/Select'

import { formatNumber } from '@/utils/formatNumber'
import { splitDecimal } from '@/utils/splitDecimal'
import { truncateToFourDecimals } from '@/utils/truncateToFourDecimals'

import { Button } from '../Button'
import Icon from '../Icon'
import { Loading } from '../Loading'
import { Title } from '../Title'
import './Exchange.scss'

interface IExchangeProps {
  className?: string
}

const cnExchange = cn('Exchange')

export const Exchange: FC<IExchangeProps> = ({ className }) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const exchangeRef = useRef<HTMLFormElement>(null)
  const { handleServerError } = useHandleServerError()

  const exchange = useAppSelector(exchangeSelector)
  const user = useAppSelector(userSelector)

  const [activeInput, setActiveInput] = useState<'from' | 'to' | null>(null)
  const [fiats, setFiats] = useState<IFiat[]>([])
  const [currencies, setCurrencies] = useState<{ label: string }[]>([])

  const [form, setForm] = useState<FormExchangeModel>(new FormExchangeModel())
  const [exchangeData, setExchangeData] = useState<IExchangeResponse | null>(
    null,
  )
  const [available, setAvailable] = useState<string>('')
  const [rate, setRate] = useState<string>('')
  // const [limit, setLimit] = useState<ILimit | null>(null)
  const [isDisabledBtn, setDisabledBtn] = useState<boolean>(false)
  const [isCalculation, setCalculation] = useState<boolean>(false)
  const [from, setFrom] = useState<{ first: string; last: string }>({
    first: '0',
    last: '00',
  })
  const [to, setTo] = useState<{ first: string; last: string }>({
    first: '0',
    last: '00000000',
  })
  const [fees, setsFees] = useState<{
    fee_network: number
    fee: number
    fee_code: string
  } | null>(null)

  useEffect(() => {
    if (activeInput === 'from') {
      setFocus('from')
    } else if (activeInput === 'to') {
      setFocus('to')
    }
  }, [activeInput])

  useEffect(() => {
    getCourse()
  }, [])

  const getCourse = async () => {
    try {
      await dispatch(getCourseThunk()).unwrap()
    } catch (error) {
      handleServerError(error)
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node
      if (exchangeRef.current && !exchangeRef.current.contains(target)) {
        setActiveInput(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setFocus,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormExchangeModel>({
    mode: 'onChange',
    defaultValues: new FormExchangeModel(),
  })

  const onChangeDirection = (fiat: string, crypto: string) => {
    return `${fiat}/${crypto}`
  }

  const onChangePayment = (
    direction: string,
    exchange: IExchangeResponse,
  ): number | null => {
    const courses = exchange['courses']['Buy']
    const course = courses[direction]

    if (course) {
      const singleKey = Object.keys(course)[0]
      const singleValue = Object.values(course)[0]

      setRate(String(parseFloat(singleValue)))

      setDisabledBtn(false)

      return +singleKey
    }

    setDisabledBtn(true)

    return 0
  }

  useEffect(() => {
    if (exchange) {
      const fiats = exchange['fiats']
      const currencies = exchange['cryptocurrencies'].map(el => ({ label: el }))

      const f = fiats[0].label
      const c = currencies[0].label
      const direct = onChangeDirection(f, c)
      const pay = onChangePayment(direct, exchange)

      setFiats(fiats)
      setCurrencies(currencies)
      setAvailable(formatNumber(+exchange['balances'][direct], 2))
      setExchangeData(exchange)

      const formData = {
        payment_id: pay ? pay : 0,
        direction: direct,
        from: '150',
        input: 'from',
        to: '',
        fiat: f,
        currency: c,
      }

      setForm(formData)
      reset(formData)
      setFrom(splitDecimal('150', 'from'))
      setCalculation(true)
      calculate({
        direction: formData.direction,
        from: formData.from,
        to: '',
        payment_id: formData.payment_id,
      })
    }
  }, [exchange])

  const onChangeForm = _.debounce((form: FormExchangeModel) => {
    const direction = onChangeDirection(form.fiat, form.currency)

    let pay: number | null = 0

    if (exchangeData) {
      setAvailable(formatNumber(+exchangeData['balances'][direction], 2))

      pay = onChangePayment(direction, exchangeData)
    }

    const calcData = {
      direction: direction,
      from: form.input === 'from' ? form.from : '',
      to: form.input === 'to' ? form.to : '',
      payment_id: pay || 0,
    }

    setForm(form)

    if (pay) {
      setCalculation(true)

      reset({ ...form, ...calcData })

      calculate(calcData)
    }
  }, 1000)

  useEffect(() => {
    const subscription = watch((form, { type, name }) => {
      if (type === 'change') {
        onChangeForm({
          ...new FormExchangeModel(),
          ...form,
          input: name === 'to' ? 'to' : 'from',
        })
      }
    })

    return () => subscription.unsubscribe()
  }, [watch, exchangeData])

  const onSubmit = async (data: FormExchangeModel) => {
    if (user && user.is_verify === 0) {
      navigate('/c/verify')
      return
    }

    const form: ICreateExchangeRequest = {
      direction: data.direction,
      payment_id: data.payment_id,
      sum: '',
    }

    if (data.input === 'from') {
      form.sum = data.from
    } else {
      form.sum = data.to
    }

    try {
      const res = await dispatch(createExchangeThunk(form)).unwrap()

      if (res.invoice_id) navigate(`/c/orders/${res.invoice_id}`)
    } catch (error) {
      handleServerError(error)
    }
  }

  const calculate = _.debounce(async (data: CalculateRequest) => {
    try {
      const res = await dispatch(calculationThunk(data)).unwrap()
      setFrom(splitDecimal(res.from, 'from'))
      setTo(splitDecimal(res.to, 'to'))

      setValue('from', String(parseFloat(res.from)))
      setValue('to', String(parseFloat(res.to)))

      const code = fiats.find(el => el.label === res.fee_code)

      setsFees({
        fee: parseFloat(res.fee),
        fee_code: code ? code.symbol : '$',
        fee_network: truncateToFourDecimals(res.fee_network),
      })
      setCalculation(false)
    } catch (error) {
      setCalculation(false)
      handleServerError(error)
    }
  }, 1000)

  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    let value = event.target.value

    value = value.replace(/[^0-9.]/g, '')

    const parts = value.split('.')
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('')
    }

    if (value.startsWith('.')) {
      value = '0' + value
    }

    event.target.value = value
  }

  const isFiat = (opt: IFiat | { label: string }): opt is IFiat => {
    return 'symbol' in opt
  }

  const SingleValue = ({
    children,
    ...props
  }: SingleValueProps<
    IFiat | { label: string },
    false,
    GroupBase<IFiat | { label: string }>
  >) => {
    const { value } = props.selectProps

    const symbol =
      isFiat(value as IFiat) && value
        ? (value as IFiat).symbol
        : (value as { label: string }).label

    const code = symbol.includes('USDT') ? 'USDT' : symbol

    return (
      <components.SingleValue {...props}>
        <span className={cnExchange('line')} {...props}>
          {isFiat(value as IFiat) ? (
            <span className={cnExchange('symbol', { fiat: true })}>
              {symbol}
            </span>
          ) : (
            <span
              className={cnExchange(
                'symbol',
                `icon-${code.toUpperCase()} ${code.toUpperCase()}-color-bg`,
              )}
            ></span>
          )}
          {children}
        </span>
      </components.SingleValue>
    )
  }

  const CustomOption = (
    props: OptionProps<
      IFiat | { label: string },
      boolean,
      GroupBase<IFiat | { label: string }>
    >,
  ) => {
    const { innerProps, isDisabled, children, data } = props

    if (isDisabled) return null

    const symbol =
      isFiat(data as IFiat) && data
        ? (data as IFiat).symbol
        : (data as { label: string }).label

    const icon = symbol.includes('USDT') ? 'USDT' : symbol

    return (
      <div className={cnExchange('line')} {...innerProps}>
        {isFiat(data as IFiat) ? (
          <span className={cnExchange('symbol', { fiat: true })}>{symbol}</span>
        ) : (
          <span
            className={cnExchange(
              'symbol',
              `icon-${icon.toUpperCase()} ${icon.toUpperCase()}-color-bg`,
            )}
          ></span>
        )}
        {children}
      </div>
    )
  }

  return (
    <form
      ref={exchangeRef}
      className={cnExchange(null, [className])}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Title>{t('exchange-title')}</Title>

      {isCalculation && (
        <motion.div
          className={cnExchange('calc')}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Loading type='circle' minHeight='100%' />
        </motion.div>
      )}

      <div className={cnExchange('field')}>
        <div className={cnExchange('top')}>
          <label className={cnExchange('key')} htmlFor='from'>
            {t('exchange-spend')}
          </label>
          <div className={cnExchange('info')}>
            1 {form.currency} = {rate} {form.fiat}
          </div>
        </div>
        <div className={cnExchange('wrap')}>
          {activeInput === 'from' ? (
            <input
              id='from'
              type='text'
              className={cnExchange('input')}
              {...register('from')}
              onInput={handleInput}
            />
          ) : (
            <div
              className={cnExchange('value')}
              onClick={() => setActiveInput('from')}
            >
              {from.first}
              <span className={cnExchange('last')}>.{from.last}</span>
            </div>
          )}

          <Controller
            name='fiat'
            control={control}
            render={({ field: { onChange, value } }) => (
              <CustomSelect
                options={fiats}
                optionValue={'label'}
                optionLabel={'label'}
                value={fiats.find(el => el.label === value)}
                onChange={v => {
                  onChange(v['label'])
                }}
                mb='none'
                SingleValue={SingleValue}
                CustomOption={CustomOption}
              />
            )}
          />
        </div>
      </div>

      <div className={cnExchange('field')}>
        <div className={cnExchange('top')}>
          <label className={cnExchange('key')} htmlFor='to'>
            I want to buy
          </label>
          <div className={cnExchange('info')}>
            {available} {form.currency} Available
          </div>
        </div>
        <div className={cnExchange('wrap')}>
          {activeInput === 'to' ? (
            <input
              id='to'
              type='text'
              className={cnExchange('input')}
              {...register('to')}
              onInput={handleInput}
            />
          ) : (
            <div
              className={cnExchange('value')}
              onClick={() => setActiveInput('to')}
            >
              {to.first}
              <span className={cnExchange('last')}>.{to.last}</span>
            </div>
          )}

          <Controller
            name='currency'
            control={control}
            render={({ field: { onChange, value } }) => (
              <CustomSelect
                options={currencies}
                optionValue={'label'}
                optionLabel={'label'}
                value={currencies.find(el => el.label === value)}
                onChange={v => {
                  onChange(v['label'])
                }}
                mb='none'
                SingleValue={SingleValue}
                CustomOption={CustomOption}
              />
            )}
          />
        </div>
      </div>

      {fees && (
        <div className={cnExchange('percent')}>
          <div className={cnExchange('icon')}>
            <Icon name='percent' />
          </div>

          {fees.fee_network && (
            <div className={cnExchange('item')}>
              <div className={cnExchange('fee')}>
                {fees.fee_code}
                {fees.fee_network}
              </div>
              <div className={cnExchange('label')}>
                {t('exchange-network-fee')}
              </div>
            </div>
          )}
          {fees.fee_network && (
            <div className={cnExchange('item')}>
              <div className={cnExchange('fee')}>
                {fees.fee_code}
                {fees.fee}
              </div>
              <div className={cnExchange('label')}>
                {t('exchange-service-fee')}
              </div>
            </div>
          )}
        </div>
      )}

      <Button
        type='submit'
        fluid
        iconPosition='right'
        disabled={isDisabledBtn}
        isLoading={isSubmitting}
      >
        {user && !!user.is_verify ? (
          <>
            {isDisabledBtn ? (
              <>{t('exchange-no-trading-pair')}</>
            ) : (
              <>
                {' '}
                {t('exchange-buy')} {form.currency}
              </>
            )}

            <Icon name='chevron-right' stroke='#fff' />
          </>
        ) : (
          <>{t('exchange-pass-kyc')}</>
        )}
      </Button>
    </form>
  )
}
